import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import { searchMedia, getTopRated } from "@/utils/media";
import { useSEO } from "@/hooks/useSEO";
import type { MediaType } from "@/types/media";

type TypeFilter = "all" | MediaType;

const typeTabs: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(initial);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    setKeyword(params.get("q") ?? "");
  }, [params]);

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    const list = searchMedia(keyword);
    return typeFilter === "all" ? list : list.filter((item) => item.type === typeFilter);
  }, [keyword, typeFilter]);

  const hot = getTopRated(undefined, 8);
  const hasKeyword = keyword.trim().length > 0;

  useSEO({
    title: hasKeyword ? `“${keyword.trim()}”的搜索结果 - 西瓜影院` : "全站搜索 - 西瓜影院",
    description: "西瓜影院全站搜索，输入片名、演员或导演关键词，快速查找你想要的电影与电视剧资源。",
    keywords: "影视搜索,电影搜索,电视剧搜索,西瓜影院",
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="mx-auto px-4 py-8 md:px-8 md:py-10">
      <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-200">搜索结果</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">全站搜索 - 西瓜影院</h1>
        <p className="mt-2 text-sm leading-6 text-foreground-400">
          输入片名、演员或导演关键词，快速查找你想看的电影与电视剧。
        </p>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
        <div className="relative w-full">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-lg" aria-hidden="true"></i>
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="text"
            name="q"
            placeholder="搜索电影、电视剧、演员或导演…"
            className="h-12 w-full rounded-md border border-background-300 bg-background-100 pl-12 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
          />
        </div>
        <button
          type="submit"
          className="h-12 shrink-0 rounded-md bg-primary-500 px-8 text-sm font-500 text-background-950 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {hasKeyword && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-400">
            关键词 “<span className="font-700 text-accent-400">{keyword.trim()}</span>” 共找到{" "}
            <span className="font-700 text-accent-400">{results.length}</span> 条结果
          </p>
          <div className="flex gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {typeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setTypeFilter(tab.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-500 transition-colors ${
                  typeFilter === tab.key
                    ? "bg-primary-500 text-background-950"
                    : "text-foreground-400 hover:text-foreground-950"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!hasKeyword ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-4 font-700 text-foreground-950">输入关键词开始搜索</h2>
          <p className="mt-2 text-sm text-foreground-500">例如“星际”“悬疑”“林浩然”，或直接浏览下方热门推荐。</p>
        </div>
      ) : results.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {results.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-unhappy-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-4 font-700 text-foreground-950">没有找到相关影视资源</h2>
          <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐。</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setParams({});
              }}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-500 text-background-950 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true"></i>
              重置搜索
            </button>
            <Link
              to="/category/movie"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-background-300 px-5 text-sm font-500 text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
            >
              浏览电影分类
            </Link>
          </div>
        </div>
      )}

      {( !hasKeyword || results.length === 0 ) && (
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
            <span className="flex h-5 w-1 rounded-full bg-accent-500" />
            热门推荐
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {hot.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}